import { filenameFromContentDisposition } from "@lib/download"

import { apiClient, TRANSFER_TIMEOUT_MS } from "./client"

import type {
  AssignmentContentResponse,
  BulkReopenOut,
  ContentEditItem,
  Material,
  MaterialAssignment,
  MaterialAssignmentListResponse,
  MaterialCut,
  MaterialItemTypeCode,
  MaterialListResponse,
  MaterialReferenceRef,
  MaterialProgress,
  MaterialQuestionCount,
  MaterialStatus,
  MaterialType,
  MaterialTypesResponse,
  EducationLevel,
  ProgrammingLanguage,
  RegenerationReason,
  ReopenOut,
  StarterCode,
} from "@/types"

export type MaterialStatusFilter = MaterialStatus | "in_progress" | "all"

/**
 * PRD-05 §FR-6 (2026-08-05) — sorting is applied client-side by
 * `materialsFiltering.sortMaterials`, so this union is the SPA's contract with
 * itself, not the server's. `recent` / `classroom` / `type` were dropped and
 * `name` added; the backend still accepts its own wider `VALID_SORTS` for any
 * other caller.
 */
export type MaterialSort = "priority" | "send_date_asc" | "send_date_desc" | "name"

/**
 * Params for the paged `list` call, still used by surfaces that need a
 * server-filtered slice (the gradebook's material picker).
 *
 * Deliberately carries no `sort`: /materiales sorts client-side now, and its
 * `name` option is not in the backend's `VALID_SORTS` — forwarding it would
 * earn a 422. Server-side ordering falls back to the endpoint's own default.
 */
export interface ListMaterialsParams {
  q?: string
  status?: MaterialStatusFilter
  classroom_id?: string
  material_type?: MaterialType
  page?: number
  page_size?: number
}

// PRD-42 §FR-1 — generation is always immediate, so the create payload
// no longer carries `send_date` / `dispatch_mode`. The send day is
// chosen at approval (see `approve` below).
export interface GenerateMaterialPayload {
  classroom_id: string
  material_type: MaterialType
  topic: string
  question_count: MaterialQuestionCount
  notes: string
  // Question codes for the four question types; exercise codes for `codigo`
  // (manifest v12 validates against the type's OWN contract choices).
  item_type: MaterialItemTypeCode
  // PRD-49 — sent only when material_type is `codigo`; other types reject
  // it with `language_not_supported_for_type`.
  language?: ProgrammingLanguage
  topic_subject_override: boolean
  // PRD-64 FR-18 — up to REFERENCE_MAX_PER_MATERIAL of the professor's sources.
  references?: MaterialReferenceRef[]
}

export interface GenerateMaterialResponse {
  material_id: string
  engine_execution_name: string
  status: MaterialStatus
  total_assignments: number
  education_level: EducationLevel
  // PRD-63 — the version produced, and on a regeneration the students
  // enrolled since the previous one who now get it too.
  round: number
  new_students: number
}

// PRD-63 — mirrors apps/backend/api/schemas.py::RegenerateMaterialIn.
export interface RegenerateMaterialPayload {
  reasons: RegenerationReason[]
  comment: string
}

export interface RetryFailedResponse {
  retried: number
  engine_execution_name: string | null
}

export interface RetryAssignmentResponse {
  assignment_id: string
  engine_execution_name: string
  attempts: number
}

function cleanParams(params: ListMaterialsParams): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  if (params.q?.trim()) out.q = params.q.trim()
  if (params.status && params.status !== "all") out.status = params.status
  if (params.classroom_id) out.classroom_id = params.classroom_id
  if (params.material_type) out.material_type = params.material_type
  if (params.page && params.page > 1) out.page = params.page
  if (params.page_size) out.page_size = params.page_size
  return out
}

export const materialsApi = {
  // Single authoritative source for per-type item-count bounds
  // (min/max/default). The request modal derives its validation from
  // this instead of a hand-maintained TS mirror, so a CATALOG change on
  // the backend (apps/backend/core/material_types.py) never needs a
  // matching frontend edit to stay in sync.
  types: () => apiClient.get<MaterialTypesResponse>("/material-types/").then((r) => r.data),

  list: (params: ListMaterialsParams = {}) =>
    apiClient
      .get<MaterialListResponse>("/materials/", { params: cleanParams(params) })
      .then((r) => r.data),

  /**
   * Every material in one classroom scope (or every classroom when
   * `classroomId` is null), for the client-side filter panel.
   *
   * PRD-05 §FR-6 (2026-08-05) — the panel needs the whole set in memory to
   * compute cross-faceted counts; a paged response can only ever describe one
   * filter combination. `MaterialOut` carries no `questions` blob, so a row is
   * light enough to make this reasonable at the scale the PRD targets (~80
   * materials per professor).
   *
   * The server clamps `page_size` at 100 (PAGE_SIZE_MAX), so this walks pages
   * until it has `total`. The page cap is a guard against a bad `total`
   * spinning the loop forever, not a product decision — see the plan's
   * follow-ups if a professor ever approaches it.
   *
   * PRD-62 — the server hides archived materials unless asked; `archived`
   * returns ONLY them (never both at once), for the "Archivados" section.
   */
  listAll: async (
    classroomId?: string | null,
    options: { archived?: boolean } = {},
  ): Promise<Material[]> => {
    const PAGE_SIZE = 100
    const MAX_PAGES = 10
    const rows: Material[] = []
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const res = await apiClient
        .get<MaterialListResponse>("/materials/", {
          params: {
            ...(classroomId ? { classroom_id: classroomId } : {}),
            ...(options.archived ? { archived: true } : {}),
            page,
            page_size: PAGE_SIZE,
          },
        })
        .then((r) => r.data)
      rows.push(...res.items)
      if (rows.length >= res.total || res.items.length === 0) break
    }
    return rows
  },

  /**
   * PRD-62 — how many archived materials the scope holds, without fetching
   * them: one row per page, and `total` is the answer. Lets the collapsed
   * "Archivados" header state its count (or not render at all) while the list
   * itself loads only when she opens it.
   */
  countArchived: (classroomId?: string | null) =>
    apiClient
      .get<MaterialListResponse>("/materials/", {
        params: {
          ...(classroomId ? { classroom_id: classroomId } : {}),
          archived: true,
          page_size: 1,
        },
      })
      .then((r) => r.data.total),

  get: (id: string) => apiClient.get<Material>(`/materials/${id}`).then((r) => r.data),

  // PRD-11 — real-time aggregate progress for the strip + per-student grid.
  progress: (id: string) =>
    apiClient.get<MaterialProgress>(`/materials/${id}/progress`).then((r) => r.data),

  generate: (payload: GenerateMaterialPayload) =>
    apiClient.post<GenerateMaterialResponse>("/materials/generate", payload).then((r) => r.data),

  // PRD-42 §FR-2 — the approval names both the content and the moment.
  // ``sendDate`` null (or today) delivers immediately, exactly like the
  // old happy path; a FUTURE date parks the material in ``por_enviar``
  // and the 08:00 MX sweep delivers it that morning. A past date is
  // rejected with 422 ``material_send_date_too_soon``.
  // ``studentUploadDeadline`` is anchored to that chosen send date:
  // backend defaults to send_date + 1d and caps at send_date + 60d.
  // Both travel as query params per the endpoint's signature.
  approve: (
    id: string,
    options?: { sendDate?: string | null; studentUploadDeadline?: string | null },
  ) => {
    const params: Record<string, string> = {}
    if (options?.sendDate) params.send_date = options.sendDate
    if (options?.studentUploadDeadline) {
      params.student_upload_deadline = options.studentUploadDeadline
    }
    return apiClient
      .post<Material>(`/materials/${id}/approve`, undefined, { params })
      .then((r) => r.data)
  },

  cancel: (id: string) => apiClient.post<Material>(`/materials/${id}/cancel`).then((r) => r.data),

  // ── PRD-42 §FR-6 — managing a pending send ────────────────────────
  // All three act on a ``por_enviar`` material and answer 409
  // ``material_not_scheduled`` when the status moved on (double click,
  // or the morning sweep delivered it while the tab was open).

  /** Deliver a scheduled material now, ahead of its send day. */
  sendNow: (id: string) =>
    apiClient.post<Material>(`/materials/${id}/send-now`).then((r) => r.data),

  /** Move the send to another day. 422 if the date isn't in the future. */
  reschedule: (id: string, sendDate: string) =>
    apiClient
      .post<Material>(`/materials/${id}/reschedule`, undefined, {
        params: { send_date: sendDate },
      })
      .then((r) => r.data),

  /** Drop the pending send; the material returns to `revision`. */
  cancelSchedule: (id: string) =>
    apiClient.post<Material>(`/materials/${id}/cancel-schedule`).then((r) => r.data),

  // PRD-63 — a new version of the same material, with the professor's feedback.
  regenerate: ({ id, ...payload }: RegenerateMaterialPayload & { id: string }) =>
    apiClient
      .post<GenerateMaterialResponse>(`/materials/${id}/regenerate`, payload)
      .then((r) => r.data),

  retryFailed: (id: string) =>
    apiClient.post<RetryFailedResponse>(`/materials/${id}/retry-failed`).then((r) => r.data),

  // PRD-66 — generate this material for the students of the group who have
  // no copy. `retried` is how many copies were created (0 = nobody missing,
  // e.g. another tab already did it).
  backfillMissing: (id: string) =>
    apiClient.post<RetryFailedResponse>(`/materials/${id}/backfill-missing`).then((r) => r.data),

  // PRD-13 §FR-5.3 — re-enqueue every failed/bounced email send for
  // a Material in one click. Skips students without an email on file.
  retryFailedEmails: (id: string) =>
    apiClient
      .post<{ retried: number; skipped_no_email: number }>(`/materials/${id}/email/retry-failed`)
      .then((r) => r.data),

  // PRD-13 follow-up — RE-SEND (not retry) the delivery email to every
  // student whose prior send succeeded. Distinct from retryFailedEmails
  // above: that one targets failed/bounced; this one targets the
  // dominant ``sent`` case + sweeps any failed/bounced for completeness.
  // ``skipped_complained`` is always 0 today (filter excludes it
  // server-side) — exposed so a future opt-in flow can surface it.
  // PRD-42 §FR-8.2 — answers 422 ``material_not_sent`` unless the
  // material actually reached students.
  resendAllEmails: (id: string) =>
    apiClient
      .post<{
        resent: number
        skipped_no_email: number
        skipped_complained: number
      }>(`/materials/${id}/email/resend-all`)
      .then((r) => r.data),

  // PRD-73 §FR-4 — «Dar más tiempo» to several students at once. 1–200 ids,
  // every one of this material and this professor (422 otherwise). A row the
  // server refuses or finds already open comes back in ``skipped`` with its
  // reason; the sweep goes on. No link is returned: the emails go out paced.
  reopen: (id: string, assignmentIds: string[]) =>
    apiClient
      .post<BulkReopenOut>(`/materials/${id}/reopen`, { assignment_ids: assignmentIds })
      .then((r) => r.data),

  // PRD-62 §FR-1.3 — a SENT material answers 409
  // ``material_sent_archive_instead``: its grades would outlive it. Archive it.
  delete: (id: string) =>
    apiClient.delete<{ success: boolean }>(`/materials/${id}`).then((r) => r.data),

  // ── PRD-62 — archive / restore ────────────────────────────────────
  // Only a sent material can be archived (409 ``material_not_archivable``
  // otherwise). Both are idempotent and answer the updated material — whose
  // `review_summary` is null, like every non-detail read.

  /** Take a sent material out of every grade and close its portal link. */
  archive: (id: string) => apiClient.post<Material>(`/materials/${id}/archive`).then((r) => r.data),

  /** Put it back: it counts again and a still-valid portal link works again. */
  restore: (id: string) => apiClient.post<Material>(`/materials/${id}/restore`).then((r) => r.data),

  // PRD-16 — bulk download. Returns the ZIP as a blob plus the
  // server-suggested filename parsed from Content-Disposition. The
  // caller wires the blob into a temporary <a download> + revokes the
  // object URL so the browser save dialog fires.
  downloadBulk: async (id: string) => {
    const res = await apiClient.get<Blob>(`/materials/${id}/download`, {
      responseType: "blob",
      timeout: TRANSFER_TIMEOUT_MS,
    })
    return {
      blob: res.data,
      // "archivo.zip" when the header is absent or unparseable, so the save
      // dialog still works.
      filename:
        filenameFromContentDisposition(
          (res.headers as Record<string, string>)["content-disposition"],
        ) ?? "archivo.zip",
    }
  },

  // PRD-61 §FR-5.1 — which evaluation cut this material counts for, and the
  // cuts it could be moved to. `period` null = the school has no calendar.
  getCut: (id: string) => apiClient.get<MaterialCut>(`/materials/${id}/cut`).then((r) => r.data),

  // `cut: null` puts it back on its due date. Same shape back as `getCut`.
  setCut: (id: string, cut: number | null) =>
    apiClient.patch<MaterialCut>(`/materials/${id}/cut`, { cut }).then((r) => r.data),

  // PRD-65 — recompile every student with a saved draft, under the existing
  // in-flight cap. 422 ``material_no_dirty_edits`` when nobody is dirty.
  // PRD-65 — mirrors api/schemas.py::CompileEditsOut. `remaining`: dirty
  // students beyond the professor's free in-flight slots, for a later pass.
  compileEdits: (id: string) =>
    apiClient
      .post<{
        task_run_ids: string[]
        count: number
        remaining: number
      }>(`/materials/${id}/compile-edits`)
      .then((r) => r.data),
}

export const materialAssignmentsApi = {
  // List assignments for a Material — used by the per-student grid.
  // Returns a bare array (the backend doesn't paginate this surface — a
  // classroom is at most a couple of hundred students).
  forMaterial: (materialId: string) =>
    apiClient
      .get<MaterialAssignment[]>("/material-assignments/", {
        params: { material_id: materialId },
      })
      .then((r) => r.data),

  // Per-student detail (rarely used directly from the UI; the grid pulls
  // from /api/materials/{id}/progress + the embedded list on the Material).
  get: (id: string) =>
    apiClient.get<MaterialAssignment>(`/material-assignments/${id}`).then((r) => r.data),

  // For <a target="_blank"> link clicks: browser follows the 302 to the
  // signed URL transparently. JWT travels via the apiClient interceptor
  // when the SPA calls fetch via authed paths — but a raw <a> click does
  // NOT carry the bearer token, so this URL is only useful when the user
  // is already pre-authed via cookie or for a redirect we initiate from
  // an authed XHR (rare). Prefer ``getPdfUrl`` below for in-app preview.
  pdfUrl: (id: string, role: "student" | "professor") =>
    `${apiClient.defaults.baseURL ?? ""}/material-assignments/${id}/pdf?role=${role}`,

  // Fetch the signed URL as JSON via the authed apiClient. Use this from
  // the iframe preview drawer and from the StudentMaterialsSection's
  // "Ver material" link (call this on click, then window.open the URL).
  getPdfUrl: (id: string, role: "student" | "professor") =>
    apiClient
      .get<{ url: string; expires_at: string }>(`/material-assignments/${id}/pdf-url`, {
        params: { role },
      })
      .then((r) => r.data),

  // PRD-16 — single-PDF download. Mints a signed URL whose
  // ``response-content-disposition`` forces a save dialog with a
  // Spanish-readable filename ("Nombre - Tipo - Tema.pdf"). The caller
  // points an <a href={url} download> at the URL and clicks it.
  getDownloadUrl: (id: string, role: "student" | "professor") =>
    apiClient
      .get<{ url: string; expires_at: string }>(`/material-assignments/${id}/pdf-url`, {
        params: { role, download: true },
      })
      .then((r) => r.data),

  // PRD-52 §FR-9 — the `codigo` starter file's TEXT, for the in-app
  // preview. Unlike the PDFs this is not a signed URL: a browser handed a
  // signed URL to a `.py` object downloads it or paints unstyled plain
  // text, and neither is a preview. The SPA renders `content` through
  // `components/ui/CodeBlock`, where indentation survives.
  getStarterCode: (id: string) =>
    apiClient.get<StarterCode>(`/material-assignments/${id}/starter-code`).then((r) => r.data),

  // PRD-52 §FR-9 — the same file, byte-for-byte, as a blob. The caller
  // already holds the real `filename` from `getStarterCode` above and
  // passes it to the <a download> — so the save name never depends on the
  // Content-Disposition header surviving a cross-origin round-trip, and
  // never on a language→extension table (PRD-52 §FR-2).
  downloadStarterCode: (id: string) =>
    apiClient
      .get<Blob>(`/material-assignments/${id}/starter-code/download`, {
        responseType: "blob",
        timeout: TRANSFER_TIMEOUT_MS,
      })
      .then((r) => r.data),

  retry: (id: string) =>
    apiClient
      .post<RetryAssignmentResponse>(`/material-assignments/${id}/retry`)
      .then((r) => r.data),

  // PRD-13 §FR-5.1 — re-enqueue a single failed/bounced email send.
  // No engine re-run; reuses the existing student PDF in GCS.
  retryEmail: (id: string) =>
    apiClient
      .post<MaterialAssignment>(`/material-assignments/${id}/email/retry`)
      .then((r) => r.data),

  // PRD-13 follow-up — RE-SEND (not retry) a single already-delivered
  // email. Distinct intent + audit action from retryEmail: this is the
  // "student deleted the email" path, not the "previous send failed"
  // path. Backend allows sent/bounced/failed (the three states with a
  // prior send attempt); the SPA shows the affordance on sent rows.
  resendEmail: (id: string) =>
    apiClient
      .post<MaterialAssignment>(`/material-assignments/${id}/email/resend`)
      .then((r) => r.data),

  // PRD-73 §FR-3 — «Dar más tiempo» to one student after the deadline. No
  // body. 409 ``reopen_before_deadline`` | ``reopen_scanned`` |
  // ``reopen_not_supported`` | ``material_archived``. An email failure is not
  // an error: ``email_outcome`` says so and ``portal_url`` lets the professor
  // send the link herself. ``portal_url`` is null on ``already_open``.
  reopen: (id: string) =>
    apiClient.post<ReopenOut>(`/material-assignments/${id}/reopen`).then((r) => r.data),

  // ── PRD-65 — editing one student's content before it is sent ──────
  // Mirrors apps/backend/api/material_assignments.py.

  /** The release's content, the saved draft and the derived edit state.
   *  404 ``assignment_content_unavailable`` for a release before manifest v20. */
  getContent: (id: string) =>
    apiClient
      .get<AssignmentContentResponse>(`/material-assignments/${id}/content`)
      .then((r) => r.data),

  /** Store the draft (``items: []`` clears it). 422 ``assignment_not_editable``. */
  putContentEdits: (id: string, items: ContentEditItem[]) =>
    apiClient
      .put<AssignmentContentResponse>(`/material-assignments/${id}/content-edits`, { items })
      .then((r) => r.data),

  /** Render the draft into new PDFs. 422 ``assignment_not_dirty``,
   *  409 ``assignment_compile_in_progress``, 503 ``engine_dispatch_failed``. */
  compile: (id: string) =>
    apiClient
      .post<{ task_run_id: string }>(`/material-assignments/${id}/compile`)
      .then((r) => r.data),
}

export const studentMaterialsApi = {
  list: (studentId: string, page = 1, pageSize = 25) =>
    apiClient
      .get<MaterialAssignmentListResponse>(`/students/${studentId}/materials`, {
        params: { page, page_size: pageSize },
      })
      .then((r) => r.data),
}
